import { useCallback, useReducer } from 'react'
import { useIntl } from 'react-intl'
import { useFlag } from '@unleash/proxy-client-react'
import effects from 'app/effects/effects'
import MessageAlert from 'app/components/MessageAlert'
import { useDispatch } from 'react-redux'

import {
  DELETED_MSG,
  UPDATED_MSG,
  UNSENT_EVENT,
  CANCEL_EVENT,
} from 'app/constants/messageDetail'

export const ActionType = {
  SET_SUCCESS: 'SET_SUCCESS',
  SET_TOGGLE_HISTORY_ERROR: 'SET_TOGGLE_HISTORY_ERROR',
}

const ERROR_CODE_CANCEL_NO_PERMISSION = 658

const initialState = {
  isSuccess: false,
  isToggleHistoryError: false,
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_SUCCESS:
      return {
        ...state,
        isSuccess: action.value,
      }
    case ActionType.SET_TOGGLE_HISTORY_ERROR:
      return {
        ...state,
        isToggleHistoryError: action.value,
      }
    default:
      return state
  }
}

export default function useAction(onFetch) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { formatMessage } = useIntl()
  const commonDispatch = useDispatch()
  const mes_5281_save_draft_for_polls = useFlag('mes_5281_save_draft_for_polls')

  const handleAlertError = useCallback(() => {
    return commonDispatch(
      MessageAlert.showError({
        message: formatMessage({
          id: 'error.general',
        }),
      }),
    )
  }, [commonDispatch, formatMessage])

  const handleUnsent = (id) => {
    effects.apis
      .notificationUnsend(id)
      .then(() => {
        commonDispatch(
          MessageAlert.showSuccess({
            message: formatMessage({
              id: 'notificationTimeline.card.unsend.success',
            }),
          }),
        )
        onFetch(id, UPDATED_MSG)
      })
      .catch(() => handleAlertError())
  }

  const handleCancel = (id, reason) => {
    effects.apis
      .notificationCancelEvent(id, reason)
      .then(() => {
        commonDispatch(
          MessageAlert.showSuccess({
            message: formatMessage({
              id: 'notificationTimeline.dialog.event.cancelDelete.cancel.success.alert',
            }),
          }),
        )
        onFetch(id, UPDATED_MSG)
      })
      .catch(() => handleAlertError())
  }

  const deleteDraft = (id) => {
    const deleteApi = mes_5281_save_draft_for_polls
      ? effects.apis.deleteDraftMessage(id)
      : effects.apis.deleteDraftNotification(id)

    deleteApi
      .then(() => {
        commonDispatch(
          MessageAlert.showSuccess({
            message: formatMessage({
              id: 'draft.notification.delete.success.message',
            }),
          }),
        )
        onFetch(id, DELETED_MSG)
      })
      .catch(() => handleAlertError())
  }

  const unsend = (id) => {
    handleUnsent(id)
  }

  const cancelReminder = (id) => {
    effects.apis
      .cancelReminder(id)
      .then(() => {
        commonDispatch(
          MessageAlert.showSuccess({
            message: formatMessage({
              id: 'notificationTimeline.card.cancelReminder.success',
            }),
          }),
        )
        onFetch(id, UPDATED_MSG)
      })
      .catch(() => handleAlertError())
  }

  const cancelExpiration = (id) => {
    effects.apis
      .cancelExpiration(id)
      .then(() => {
        commonDispatch(
          MessageAlert.showSuccess({
            message: formatMessage({
              id: 'notificationTimeline.card.cancelExpiration.success',
            }),
          }),
        )
        onFetch(id, UPDATED_MSG)
      })
      .catch(() => handleAlertError())
  }

  const cancelSchedule = (id) => {
    effects.apis
      .cancelScheduleNotification(id)
      .then(() => {
        commonDispatch(
          MessageAlert.showSuccess({
            message: formatMessage({
              id: 'notificationTimeline.card.cancelNotification.success',
            }),
          }),
        )
        onFetch(id, UPDATED_MSG)
      })
      .catch((error) => {
        const errorCode = error?.response?.data?.details?.code
        if (errorCode === ERROR_CODE_CANCEL_NO_PERMISSION) {
          commonDispatch(
            MessageAlert.showError({
              message: formatMessage({ id: 'error.permission.denied' }),
            }),
          )
        } else {
          handleAlertError()
        }
      })
  }

  const cancelOrCancelEvent = (id, type, reason) => {
    if (type === UNSENT_EVENT) handleUnsent(id)
    else if (type === CANCEL_EVENT) handleCancel(id, reason)
    else return
  }

  const updateApplyHistory = (id, bool) => {
    effects.apis
      .updateDisplayHistory({ notificationId: id, displayHistory: bool })
      .then(() => {
        dispatch({ type: 'SET_SUCCESS', value: true })
        commonDispatch(
          MessageAlert.showSuccess({
            message: formatMessage({
              id: 'text.change.success',
            }),
          }),
        )
      })
      .catch(() => {
        dispatch({ type: 'SET_TOGGLE_HISTORY_ERROR', value: true })
        handleAlertError()
      })
  }

  const handleIsToggleHistoryError = () => {
    dispatch({ type: 'SET_TOGGLE_HISTORY_ERROR', value: false })
  }

  const endPoll = (id) => {
    effects.apis
      .endPoll(id)
      .then(() => {
        commonDispatch(
          MessageAlert.showSuccess({
            message: formatMessage({
              id: 'text.end.poll.success',
            }),
          }),
        )
        onFetch(id, UPDATED_MSG)
      })
      .catch(() => handleAlertError())
  }

  return {
    state,
    deleteDraft,
    unsend,
    endPoll,
    cancelSchedule,
    cancelReminder,
    cancelExpiration,
    cancelOrCancelEvent,
    updateApplyHistory,
    handleIsToggleHistoryError,
  }
}
